/**
 * The firm's contact list: searching it, and what deleting from it costs
 * (2026-09-22).
 *
 * Contacts have been firm-owned since 0018/0030 — one person, referenced by
 * every project they're on. This module holds the two decisions the page
 * makes that are worth testing on their own: which contacts a search shows,
 * and what a delete is about to take with it.
 *
 * That second one is not cosmetic. Deleting a contact CASCADES to their
 * project memberships, their contractor portal links and their task
 * assignments — on every project, not just the one the designer happens to be
 * thinking about — and blanks the vendor on any punch item that named them.
 * "Are you sure?" with no numbers in it is not a question anyone can answer.
 */

import { contactSearchKey, contactTypeLabel } from '@/lib/contact-display';
import type { DesignerContactRow } from '@/types/database';

/** The four independent flags, as filter options. */
export type ContactTypeFilter = 'all' | 'client' | 'contractor' | 'subcontractor' | 'vendor';

export const CONTACT_TYPE_FILTERS: { value: ContactTypeFilter; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'client', label: 'Clients' },
  { value: 'contractor', label: 'Contractors' },
  { value: 'subcontractor', label: 'Subcontractors' },
  { value: 'vendor', label: 'Vendors' },
];

const HAS_TYPE: Record<Exclude<ContactTypeFilter, 'all'>, (c: DesignerContactRow) => boolean> = {
  client: (c) => c.is_client,
  contractor: (c) => c.is_contractor,
  subcontractor: (c) => c.is_subcontractor,
  vendor: (c) => c.is_vendor,
};

/**
 * Contacts matching a search box and a type filter, in the order given.
 *
 * The search runs over name, company, email, phone AND the type labels
 * (contactSearchKey), so typing "vendor" finds vendors — the same haystack the
 * project picker uses, rather than a second definition that drifts from it.
 */
export function searchContacts(
  contacts: DesignerContactRow[],
  query: string,
  type: ContactTypeFilter = 'all',
): DesignerContactRow[] {
  const q = query.trim().toLowerCase();
  return contacts.filter((c) => {
    if (type !== 'all' && !HAS_TYPE[type](c)) return false;
    if (q === '') return true;
    return contactSearchKey(c).includes(q);
  });
}

/** How a contact is used across the firm, for the list and the delete warning. */
export interface ContactUsage {
  /** Projects they're a member of. */
  projects: number;
  /** Live contractor portal links (one per project). */
  portalLinks: number;
  /** Scheduled tasks assigned to them. */
  assignments: number;
  /** Punch items naming them as the vendor. */
  punchItems: number;
}

export const NO_USAGE: ContactUsage = { projects: 0, portalLinks: 0, assignments: 0, punchItems: 0 };

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** "On 3 projects", or "Not on any project yet" — the list's second line. */
export function usageLabel(u: ContactUsage): string {
  if (u.projects === 0) return 'Not on any project yet';
  return `On ${plural(u.projects, 'project')}`;
}

/**
 * What deleting this contact will do, as a confirm() message — or null when
 * there is nothing to warn about and a plain confirm will do.
 *
 * Every line here is a cascade in the database (0018), not a guess about what
 * the app might do next.
 */
export function deleteContactWarning(
  contact: Pick<DesignerContactRow, 'name'>,
  u: ContactUsage,
): string {
  const lines: string[] = [];
  if (u.projects > 0) lines.push(`• removed from ${plural(u.projects, 'project')}`);
  if (u.portalLinks > 0) {
    lines.push(`• ${plural(u.portalLinks, 'contractor portal link')} stop working immediately`);
  }
  if (u.assignments > 0) lines.push(`• unassigned from ${plural(u.assignments, 'scheduled task')}`);
  if (u.punchItems > 0) {
    lines.push(`• ${plural(u.punchItems, 'punch item')} lose their vendor (the ${u.punchItems === 1 ? 'item stays' : 'items stay'})`);
  }

  const head = `Delete ${contact.name} from your contacts?`;
  if (lines.length === 0) {
    return `${head}\n\nThey aren’t used anywhere yet, so nothing else changes.`;
  }
  return `${head}\n\nThis affects every project, not just one:\n${lines.join('\n')}\n\n`
    + `To take them off a single project instead, use Remove on that project’s Contacts page.`;
}

/** One line per contact for the CSV/print export of the whole list. */
export function contactExportRow(c: DesignerContactRow, u: ContactUsage): string[] {
  return [c.name, contactTypeLabel(c), c.company ?? '', c.role ?? '', c.email ?? '', c.phone ?? '', String(u.projects)];
}
